import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Send, Paperclip, Smile, X, Reply, Bold, Italic, AtSign } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { cn } from '@/lib/utils';
import { EMOJI_LIST, saveDraft } from '../utils/format';
import { MentionPicker } from './MentionPicker';
import { VoiceRecorder } from './VoiceRecorder';
import type { ChatMessage, ParticipantBrief } from '../types';

export interface ComposerHandle {
  focus(): void;
  setText(text: string): void;
  getText(): string;
}

interface Props {
  conversationId: string;
  participants: ParticipantBrief[];
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
  onSendText: (content: string, replyToId?: string) => Promise<void>;
  onSendFiles: (files: File[], content?: string) => Promise<void>;
  onSendVoice: (blob: Blob, durationMs: number) => Promise<void>;
  onTyping?: () => void;
  sendHotkey: 'enter' | 'ctrl_enter';
}

// Task #1660 — Composer estratto dal monolite. Gestisce:
// - typing draft persistito (localStorage per conversazione)
// - mention picker (`@` → MentionPicker)
// - emoji + formattazione **bold** _italic_
// - voice recorder (VoiceRecorder)
// - upload file multipli
// - reply preview con dismiss
// - hotkey configurabile (Enter / Ctrl+Enter)

export const MessageComposer = forwardRef<ComposerHandle, Props>(function MessageComposer(props, ref) {
  const { conversationId, participants, replyTo, onCancelReply, onSendText, onSendFiles, onSendVoice, onTyping, sendHotkey } = props;
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [sending, setSending] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => taRef.current?.focus(),
    setText: (t: string) => { setText(t); saveDraft(conversationId, t); },
    getText: () => text,
  }), [conversationId, text]);

  const change = (val: string) => {
    setText(val);
    saveDraft(conversationId, val);
    onTyping?.();
    const ta = taRef.current;
    const cursor = ta?.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@(\w*)$/);
    if (atMatch) {
      setMentionOpen(true);
      setMentionQuery(atMatch[1] ?? '');
    } else {
      setMentionOpen(false);
      setMentionQuery('');
    }
  };

  const insertMention = (u: ParticipantBrief) => {
    const ta = taRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const atIdx = before.lastIndexOf('@');
    if (atIdx < 0) return;
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'utente';
    const token = `@[${name}](${u.id}) `;
    const next = before.slice(0, atIdx) + token + after;
    setText(next);
    saveDraft(conversationId, next);
    setMentionOpen(false);
    setMentionQuery('');
    setTimeout(() => {
      ta.focus();
      const pos = atIdx + token.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertFormatting = (prefix: string, suffix: string) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = text.slice(start, end);
    const next = text.slice(0, start) + prefix + selected + suffix + text.slice(end);
    change(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
    }, 0);
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await onSendText(t, replyTo?.id);
      setText('');
      saveDraft(conversationId, '');
    } finally { setSending(false); }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen) return; // MentionPicker gestisce ↑↓/Enter
    if (sendHotkey === 'ctrl_enter') {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void send(); }
    } else {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
    }
  };

  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const files = Array.from(e.target.files);
    await onSendFiles(files, text || undefined);
    setText('');
    saveDraft(conversationId, '');
    e.target.value = '';
  };

  return (
    <div className="border-t bg-white dark:bg-gray-900">
      {replyTo && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/30 border-b border-blue-100 dark:border-blue-900">
          <Reply className="h-3.5 w-3.5 text-blue-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-blue-600">{replyTo.sender_first_name}</span>
            <p className="text-xs text-blue-500/70 truncate">{replyTo.content?.substring(0, 80)}</p>
          </div>
          <button onClick={onCancelReply} className="shrink-0 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 p-1" aria-label="Annulla risposta">
            <X className="h-3.5 w-3.5 text-blue-400" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2 p-2 md:p-3">
        <div className="flex items-center gap-0.5 shrink-0">
          <input ref={fileRef} type="file" className="hidden" multiple onChange={onPickFiles} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="h-8 w-8 md:h-9 md:w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors"
            aria-label="Allega"
          >
            <Paperclip className="h-4 w-4 text-gray-400" />
          </button>
          <button onClick={() => insertFormatting('**', '**')} className="hidden md:flex h-9 w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 items-center justify-center" title="Grassetto" aria-label="Grassetto">
            <Bold className="h-4 w-4 text-gray-400" />
          </button>
          <button onClick={() => insertFormatting('_', '_')} className="hidden md:flex h-9 w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 items-center justify-center" title="Corsivo" aria-label="Corsivo">
            <Italic className="h-4 w-4 text-gray-400" />
          </button>
          <button
            onClick={() => {
              change(text + '@');
              setMentionOpen(true);
              setMentionQuery('');
              taRef.current?.focus();
            }}
            className="hidden md:flex h-9 w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 items-center justify-center"
            title="Menziona"
            aria-label="Menziona"
          >
            <AtSign className="h-4 w-4 text-gray-400" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button className="h-8 w-8 md:h-9 md:w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center" aria-label="Emoji">
                <Smile className="h-4 w-4 text-gray-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" side="top">
              <div className="grid grid-cols-8 gap-1">
                {EMOJI_LIST.map((emoji) => (
                  <button key={emoji} onClick={() => change(text + emoji)} className="h-8 w-8 rounded hover:bg-accent flex items-center justify-center text-lg hover:scale-110 transition-transform">
                    {emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <VoiceRecorder onSend={onSendVoice} />
        </div>

        <div className="flex-1 relative">
          <MentionPicker
            open={mentionOpen}
            query={mentionQuery}
            participants={participants}
            onPick={insertMention}
            onClose={() => setMentionOpen(false)}
          />
          <Textarea
            ref={taRef}
            placeholder="Scrivi un messaggio… (@ per menzionare, ⌘K per cercare)"
            value={text}
            onChange={(e) => change(e.target.value)}
            onKeyDown={onKey}
            className={cn(
              'w-full min-h-[36px] max-h-[120px] resize-none rounded-xl bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-sm py-2',
            )}
            rows={1}
          />
        </div>

        <Button
          size="icon"
          className="h-9 w-9 rounded-xl bg-blue-600 hover:bg-blue-700 shrink-0 shadow-sm"
          onClick={send}
          disabled={!text.trim() || sending}
          aria-label="Invia"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});
